import React, { useState, useEffect } from 'react';
import { ResponsiveButton, ResponsiveLayout, LoginPrompt } from './index';
import { GameSessionManager } from '../services/GameSessionManager';
import { useAuthentication } from '../hooks/useAuthentication';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationType } from '../services/NotificationService';
import { AuthMethod } from '../types';
import { GameConfigService } from '../admin/services/GameConfigService';
import './GameHub.css';

interface Game {
  id: string;
  name: string;
  description: string;
  icon: string;
  status: 'available' | 'coming-soon' | 'beta';
  playerCount: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  estimatedTime: string;
}

interface GameHubProps {
  onGameSelect: (gameId: string) => void;
}

const games: Game[] = [
  {
    id: 'tic-tac-toe',
    name: 'Tic-Tac-Toe',
    description: 'Classic 3x3 grid game. Get three in a row to win!',
    icon: '⭕',
    status: 'available',
    playerCount: '2 Players',
    difficulty: 'Easy',
    estimatedTime: '2-5 min'
  },
  {
    id: 'chess',
    name: 'Chess',
    description: 'The ultimate strategy game. Checkmate your opponent!',
    icon: '♛',
    status: 'available',
    playerCount: '1-2 Players',
    difficulty: 'Hard',
    estimatedTime: '15-60 min'
  },
  {
    id: 'checkers',
    name: 'Checkers',
    description: 'Jump over your opponent\'s pieces to win!',
    icon: '⚫',
    status: 'available',
    playerCount: '1-2 Players',
    difficulty: 'Medium',
    estimatedTime: '10-30 min'
  },
  {
    id: 'connect-four',
    name: 'Connect Four',
    description: 'Drop pieces to get four in a row vertically, horizontally, or diagonally!',
    icon: '🔴',
    status: 'available',
    playerCount: '2 Players',
    difficulty: 'Medium',
    estimatedTime: '5-15 min'
  },
  {
    id: 'battleship',
    name: 'Battleship',
    description: 'Sink your opponent\'s fleet in this classic naval strategy game!',
    icon: '🚢',
    status: 'available',
    playerCount: '2 Players',
    difficulty: 'Medium',
    estimatedTime: '15-30 min'
  },
  {
    id: 'poker',
    name: 'Poker',
    description: 'Texas Hold\'em poker with friends. Best hand wins!',
    icon: '🃏',
    status: 'available',
    playerCount: '2-8 Players',
    difficulty: 'Hard',
    estimatedTime: '15-45 min'
  },
  {
    id: 'mafia',
    name: 'Mafia',
    description: 'The Village social deduction game. Find the mafia before they eliminate everyone!',
    icon: '🎭',
    status: 'available',
    playerCount: '5-30 Players',
    difficulty: 'Medium',
    estimatedTime: '15-30 min'
  },
  // --- Indian Games ---
  {
    id: 'ludo',
    name: 'Ludo',
    description: 'Classic Indian board game. Race your tokens home before your opponents!',
    icon: '🎲',
    status: 'available',
    playerCount: '2-4 Players',
    difficulty: 'Easy',
    estimatedTime: '20-45 min'
  },
  {
    id: 'teen-patti',
    name: 'Teen Patti',
    description: 'Indian three-card poker. Bluff, bet, and win the pot!',
    icon: '🂡',
    status: 'available',
    playerCount: '3-6 Players',
    difficulty: 'Medium',
    estimatedTime: '10-30 min'
  },
  {
    id: 'carrom',
    name: 'Carrom',
    description: 'Flick the striker to pocket coins on this classic Indian tabletop game!',
    icon: '🎯',
    status: 'available',
    playerCount: '2 Players',
    difficulty: 'Medium',
    estimatedTime: '15-30 min'
  },
  {
    id: 'snakes-and-ladders',
    name: 'Snakes & Ladders',
    description: 'Roll the dice and climb ladders, but watch out for snakes!',
    icon: '🐍',
    status: 'available',
    playerCount: '2-4 Players',
    difficulty: 'Easy',
    estimatedTime: '10-20 min'
  },
  {
    id: 'pachisi',
    name: 'Pachisi',
    description: 'Ancient Indian cross-shaped board game. The royal game of strategy and luck!',
    icon: '🐚',
    status: 'available',
    playerCount: '2-4 Players',
    difficulty: 'Medium',
    estimatedTime: '20-40 min'
  },
  // --- Classic Board & Card Games ---
  {
    id: 'reversi',
    name: 'Reversi',
    description: 'Flip your opponent\'s discs to dominate the 8×8 board!',
    icon: '⚪',
    status: 'available',
    playerCount: '2 Players',
    difficulty: 'Medium',
    estimatedTime: '10-20 min'
  },
  {
    id: 'mancala',
    name: 'Mancala',
    description: 'Ancient pit-and-stone counting game. Capture the most seeds to win!',
    icon: '🫘',
    status: 'available',
    playerCount: '2 Players',
    difficulty: 'Medium',
    estimatedTime: '10-20 min'
  },
  {
    id: 'go',
    name: 'Go',
    description: 'Surround territory on the board in this ancient strategy game!',
    icon: '⬛',
    status: 'available',
    playerCount: '2 Players',
    difficulty: 'Hard',
    estimatedTime: '20-60 min'
  },
  {
    id: 'backgammon',
    name: 'Backgammon',
    description: 'Race to bear off all your checkers in this classic dice-and-strategy game!',
    icon: '🎰',
    status: 'available',
    playerCount: '2 Players',
    difficulty: 'Medium',
    estimatedTime: '15-30 min'
  },
  {
    id: 'uno',
    name: 'UNO',
    description: 'Match colors and numbers, play action cards, and be the first to empty your hand!',
    icon: '🟥',
    status: 'available',
    playerCount: '2-4 Players',
    difficulty: 'Easy',
    estimatedTime: '10-20 min'
  },
  // --- Puzzle & Solo Games ---
  {
    id: 'sudoku',
    name: 'Sudoku',
    description: 'Fill the 9×9 grid so every row, column, and 3×3 box contains 1-9!',
    icon: '🔢',
    status: 'available',
    playerCount: '1 Player',
    difficulty: 'Medium',
    estimatedTime: '10-30 min'
  },
  {
    id: '2048',
    name: '2048',
    description: 'Slide tiles to combine matching numbers and reach the 2048 tile!',
    icon: '🧮',
    status: 'available',
    playerCount: '1 Player',
    difficulty: 'Easy',
    estimatedTime: '5-15 min'
  },
  {
    id: 'minesweeper',
    name: 'Minesweeper',
    description: 'Uncover cells without hitting mines using number clues!',
    icon: '💣',
    status: 'available',
    playerCount: '1 Player',
    difficulty: 'Medium',
    estimatedTime: '5-20 min'
  },
  // --- More Coming Soon ---
  {
    id: 'rummy',
    name: 'Rummy',
    description: 'Indian 13-card Rummy — form sets and sequences to declare and win!',
    icon: '🃏',
    status: 'available',
    playerCount: '2-6 Players',
    difficulty: 'Medium',
    estimatedTime: '15-30 min'
  },
  {
    id: 'solitaire',
    name: 'Solitaire',
    description: 'Classic Klondike card game. Sort all cards into foundation piles!',
    icon: '🂠',
    status: 'available',
    playerCount: '1 Player',
    difficulty: 'Easy',
    estimatedTime: '5-15 min'
  },
  {
    id: 'dots-and-boxes',
    name: 'Dots & Boxes',
    description: 'Connect dots to complete boxes. Claim the most boxes to win!',
    icon: '⬜',
    status: 'available',
    playerCount: '2-4 Players',
    difficulty: 'Easy',
    estimatedTime: '5-15 min'
  },
  {
    id: 'hangman',
    name: 'Hangman',
    description: 'Guess the hidden word letter by letter before the figure is complete!',
    icon: '📝',
    status: 'available',
    playerCount: '1 Player',
    difficulty: 'Easy',
    estimatedTime: '2-5 min'
  },
  {
    id: 'pinball',
    name: 'Pinball',
    description: 'Classic arcade pinball with multiple tables, combos, and multiball!',
    icon: '🎯',
    status: 'available',
    playerCount: '1 Player',
    difficulty: 'Medium',
    estimatedTime: '5-15 min'
  },
  {
    id: 'chaupar',
    name: 'Chaupar',
    description: 'The royal Indian board game of emperors. Permanent captures, team strategy, and 3 dice!',
    icon: '🏰',
    status: 'available',
    playerCount: '4 Players (2 Teams)',
    difficulty: 'Hard',
    estimatedTime: '30-60 min'
  },
  {
    id: 'golf-card-game',
    name: 'Golf Card Game',
    description: 'Flip, swap, and match cards in this strategic 9-round card game. Lowest score wins!',
    icon: '🏌️',
    status: 'available',
    playerCount: '1-4 Players',
    difficulty: 'Medium',
    estimatedTime: '20-45 min'
  },
  {
    id: 'court-splitter',
    name: 'Court Splitter',
    description: 'Track badminton court payments and split costs with friends!',
    icon: '🏸',
    status: 'beta',
    playerCount: '2-20 Players',
    difficulty: 'Easy',
    estimatedTime: '1 min'
  },
  {
    id: 'wordle',
    name: 'Daily Word',
    description: 'Guess the 5-letter word in 6 tries with color-coded hints!',
    icon: '🟩',
    status: 'available',
    playerCount: '1 Player',
    difficulty: 'Medium',
    estimatedTime: '5 min'
  },
  {
    id: 'word-game',
    name: 'Word Clash',
    description: 'Build words on a grid and outscore your AI opponent!',
    icon: '📝',
    status: 'available',
    playerCount: '1-2 Players',
    difficulty: 'Medium',
    estimatedTime: '15-30 min'
  },
  {
    id: 'yahtzee',
    name: 'Yahtzee',
    description: 'Roll dice, pick categories, and chase the perfect Yahtzee!',
    icon: '🎲',
    status: 'available',
    playerCount: '1-2 Players',
    difficulty: 'Medium',
    estimatedTime: '15-20 min'
  },
  {
    id: 'billiards',
    name: '8-Ball Pool',
    description: 'Realistic physics-based billiards with aim and power control!',
    icon: '🎱',
    status: 'available',
    playerCount: '1-2 Players',
    difficulty: 'Medium',
    estimatedTime: '10-20 min'
  },
  {
    id: 'trivia',
    name: 'Trivia Quiz',
    description: 'Test your knowledge across 6 categories against an AI opponent!',
    icon: '🧠',
    status: 'available',
    playerCount: '1-2 Players',
    difficulty: 'Medium',
    estimatedTime: '10-15 min'
  }
];

export const GameHub: React.FC<GameHubProps> = ({ onGameSelect }) => {
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [gamesPlayed, setGamesPlayed] = useState(0);
  const [disabledGames, setDisabledGames] = useState<Set<string>>(new Set());
  
  const gameSessionManager = GameSessionManager.getInstance();
  const gameConfigService = GameConfigService.getInstance();
  const { 
    isAuthenticated, 
    user, 
    login, 
    logout, 
    createProfile, 
    isLoading,
    signUp,
    signIn,
    resetPassword,
    signInWithYahoo,
    signInWithFacebook
  } = useAuthentication();
  const { addNotification } = useNotifications();

  useEffect(() => {
    // Check if login prompt should be shown
    const shouldShow = gameSessionManager.shouldShowLoginPrompt();
    const totalGames = gameSessionManager.getCompletedGamesCount();
    
    setGamesPlayed(totalGames);
    
    if (shouldShow && !isAuthenticated) {
      setShowLoginPrompt(true);
      gameSessionManager.markLoginPromptShown();
    }

    // Load disabled games from admin config
    setDisabledGames(gameConfigService.getDisabledGames());
  }, [gameSessionManager, isAuthenticated, gameConfigService]);

  const handleGameClick = (game: Game) => {
    if (game.status === 'available') {
      // Start tracking the game session
      const gameId = gameSessionManager.startGameSession(game.id);
      console.log(`🎮 Starting ${game.name} session: ${gameId}`);
      
      // Navigate to the game
      onGameSelect(game.id);
    }
  };

  const handleLogin = async () => {
    try {
      await login(AuthMethod.GUEST); // Use the correct enum value
      setShowLoginPrompt(false);
      gameSessionManager.resetLoginPromptState();
      
      addNotification({
        type: NotificationType.SUCCESS,
        title: 'Welcome!',
        message: 'Successfully logged in. Your progress will now be saved!',
        duration: 5000
      });
    } catch (error) {
      addNotification({
        type: NotificationType.ERROR,
        title: 'Login Failed',
        message: 'Unable to log in. Please try again.',
        duration: 5000
      });
    }
  };

  const handlePostponeLogin = () => {
    gameSessionManager.postponeLoginPrompt();
    setShowLoginPrompt(false);
    
    addNotification({
      type: NotificationType.INFO,
      title: 'Login Postponed',
      message: 'We\'ll ask again in 24 hours. Keep enjoying the games!',
      duration: 3000
    });
  };

  const handleDismissLogin = () => {
    gameSessionManager.permanentlyDismissLoginPrompt();
    setShowLoginPrompt(false);
    
    addNotification({
      type: NotificationType.INFO,
      title: 'Got it!',
      message: 'You can always create an account later from the settings.',
      duration: 3000
    });
  };

  const handleCloseLoginPrompt = () => {
    setShowLoginPrompt(false);
  };

  const getStatusBadge = (status: Game['status']) => {
    switch (status) {
      case 'available':
        return <span className="status-badge available">Available</span>;
      case 'beta':
        return <span className="status-badge beta">Beta</span>;
      case 'coming-soon':
        return <span className="status-badge coming-soon">Coming Soon</span>;
      default:
        return null;
    }
  };

  // Get effective status considering admin disable
  const getEffectiveStatus = (game: Game): Game['status'] => {
    if (disabledGames.has(game.id)) return 'coming-soon';
    return game.status;
  };

  const getEffectiveStatusBadge = (game: Game) => {
    if (disabledGames.has(game.id)) {
      return <span className="status-badge coming-soon">Under Maintenance</span>;
    }
    return getStatusBadge(game.status);
  };

  const getDifficultyColor = (difficulty: Game['difficulty']) => {
    switch (difficulty) {
      case 'Easy':
        return 'difficulty-easy';
      case 'Medium':
        return 'difficulty-medium';
      case 'Hard':
        return 'difficulty-hard';
      default:
        return '';
    }
  };

  // Calculate stats dynamically
  const availableGamesCount = games.filter(game => game.status === 'available').length;
  const comingSoonCount = games.filter(game => game.status === 'coming-soon').length;

  return (
    <ResponsiveLayout maxWidth="xl" centered>
      <div className="game-hub">
        <header className="hub-header">
          <h1>🎮 PlayHub Games</h1>
          <p className="hub-subtitle">Choose your game and start playing with friends!</p>
        </header>

        <div className="games-grid">
          {games.map((game) => {
            const effectiveStatus = getEffectiveStatus(game);
            const isDisabled = effectiveStatus !== 'available';
            return (
            <div
              key={game.id}
              className={`game-card ${isDisabled ? 'disabled' : ''}`}
              onClick={() => !isDisabled && handleGameClick(game)}
            >
              <div className="game-icon">{game.icon}</div>
              
              <div className="game-info">
                <div className="game-header">
                  <h3 className="game-name">{game.name}</h3>
                  {getEffectiveStatusBadge(game)}
                </div>
                
                <p className="game-description">{game.description}</p>
                
                <div className="game-meta">
                  <div className="meta-item">
                    <span className="meta-label">Players:</span>
                    <span className="meta-value">{game.playerCount}</span>
                  </div>
                  
                  <div className="meta-item">
                    <span className="meta-label">Difficulty:</span>
                    <span className={`meta-value ${getDifficultyColor(game.difficulty)}`}>
                      {game.difficulty}
                    </span>
                  </div>
                  
                  <div className="meta-item">
                    <span className="meta-label">Time:</span>
                    <span className="meta-value">{game.estimatedTime}</span>
                  </div>
                </div>
              </div>
              
              <div className="game-actions">
                {!isDisabled ? (
                  <ResponsiveButton variant="primary" size="small">
                    Play Now
                  </ResponsiveButton>
                ) : (
                  <ResponsiveButton variant="outline" size="small" disabled>
                    {disabledGames.has(game.id) ? 'Maintenance' : game.status === 'coming-soon' ? 'Coming Soon' : 'Beta'}
                  </ResponsiveButton>
                )}
              </div>
            </div>
            );
          })}
        </div>

        <footer className="hub-footer">
          <div className="stats-section">
            <h3>Platform Stats</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">{availableGamesCount}</span>
                <span className="stat-label">Games Available</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{comingSoonCount}</span>
                <span className="stat-label">More Coming Soon</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">∞</span>
                <span className="stat-label">Fun Guaranteed</span>
              </div>
            </div>
          </div>
          
          <div className="features-section">
            <h3>Platform Features</h3>
            <div className="features-list">
              <div className="feature-item">
                <span className="feature-icon">🌐</span>
                <span>Cross-browser multiplayer</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">📱</span>
                <span>Mobile responsive</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">🌙</span>
                <span>Dark mode support</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">⚡</span>
                <span>Real-time gameplay</span>
              </div>
            </div>
          </div>
          
          <div className="legal-section">
            <div className="legal-links">
              <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="legal-link">
                Privacy Policy
              </a>
              <span className="legal-separator">•</span>
              <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" className="legal-link">
                Terms of Service
              </a>
            </div>
            <p className="copyright">© 2026 PlayHub Games. All rights reserved.</p>
          </div>
        </footer>
      </div>

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <LoginPrompt
          gamesPlayed={gamesPlayed}
          onLogin={handleLogin}
          onPostpone={handlePostponeLogin}
          onDismiss={handleDismissLogin}
          onClose={handleCloseLoginPrompt}
        />
      )}

    </ResponsiveLayout>
  );
};

export default GameHub;